import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const LOGO = "https://e7.pngegg.com/pngimages/1016/955/png-clipart-mandala-logo-quran-illustration-text-logo-thumbnail.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Selamat datang");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Login gagal");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col md:flex-row">
      {/* Left panel - branding */}
      <div className="hidden md:flex md:w-1/2 bg-black text-white relative overflow-hidden">
        <div className="absolute inset-0 mandala-bg opacity-30" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Rabtah" className="w-12 h-12 object-contain invert" />
            <div>
              <div className="font-display font-black text-2xl tracking-tighter leading-none">RABTAH</div>
              <div className="label-overline mt-1 text-neutral-400">Data Center</div>
            </div>
          </div>
          <div>
            <h1 className="font-display font-black text-5xl leading-[0.95] tracking-tighter mb-6">
              Data Pesantren.<br />Satu Ruang.<br />Satu Sumber.
            </h1>
            <p className="text-neutral-400 max-w-md leading-relaxed">
              Kelola direktori objek Mad’u dan laporan kunjungan dalam satu
              sistem terstruktur — hitam putih, presisi, tanpa kebisingan.
            </p>
          </div>
          <div className="label-overline text-neutral-500" data-testid="login-copyright-desktop">© 2026 PT. Cloud Rise Technology</div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm">
          <div className="md:hidden flex items-center gap-3 mb-10">
            <img src={LOGO} alt="Rabtah" className="w-12 h-12 object-contain" />
            <div>
              <div className="font-display font-black text-2xl tracking-tighter leading-none">RABTAH</div>
              <div className="label-overline mt-1">Data Center</div>
            </div>
          </div>

          <div className="mb-8">
            <div className="label-overline mb-3">Masuk</div>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter leading-none">
              Selamat datang kembali.
            </h2>
            <p className="text-sm text-neutral-500 mt-3">Masuk untuk mengelola data objek Mad’u.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="label-overline">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 border-black rounded-none"
                placeholder="admin@rabtah.id"
                autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="label-overline">Password</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-12 border-black rounded-none"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="w-full h-12 rounded-none bg-black text-white hover:bg-white hover:text-black hover:border hover:border-black transition-colors duration-150 font-semibold tracking-wide"
            >
              {submitting ? "MEMPROSES..." : "MASUK"}
            </Button>
          </form>

          <div className="mt-10 pt-6 border-t border-neutral-200 text-xs text-neutral-500">
            Sistem internal. Hanya admin yang berwenang boleh masuk.
          </div>
          <div className="mt-6 label-overline text-neutral-400 text-center md:text-left" data-testid="login-copyright">
            © 2026 PT. Cloud Rise Technology
          </div>
        </div>
      </div>
    </div>
  );
}
